import React, {FC, useEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {
    Box,
    Button,
    CloseButton,
    Divider,
    Flex,
    HStack,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Progress,
    Spinner,
    Text,
    VStack,
    Badge,
    useToast
} from "@chakra-ui/react";
import {FiArrowDown, FiCheck, FiX, FiAlertTriangle} from "react-icons/fi";
import {MdStop} from "react-icons/md";
import {Order, InvoiceCompactDTO, OrderTypes, ReqOrderConfig} from "../../shared/entities/Order/Order";
import {OrderController} from "../../controllers/OrderController";
import IconWithTooltip from "../../components/Icon/IconWithTooltip";

const invoiceHasLocalPrintProof = (o: InvoiceCompactDTO) => {
    if ((Number(o.printPressed) || 0) > 0) return true;
    if (Number(o.paymentStatus) === OrderTypes.CARD_PAID && (Number(o.cardPaymentDetailsPrintCount) || 0) > 0) {
        return true;
    }
    return false;
};

// Тип для статуса отправки инвойса
export interface InvoiceSendStatus {
    id: number;
    dealershipName: string;
    status: 'pending' | 'sending' | 'success' | 'error' | 'warning';
    error?: string;
}

interface InvoiceSendModalProps {
    isOpen: boolean;
    onClose: () => void;
    orders: Order[];
    buildFilters: (order?: any) => ReqOrderConfig;
    flagReminder?: boolean;
    onSuccess?: () => void;
    countSelected?: number;
}

export const InvoiceSendModal: FC<InvoiceSendModalProps> = ({
                                                                isOpen,
                                                                onClose,
                                                                orders,
                                                                buildFilters,
                                                                flagReminder = false,
                                                                onSuccess,
                                                                countSelected = 0
                                                            }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const orderController = new OrderController(dispatch);

    const [invoiceSendStatuses, setInvoiceSendStatuses] = useState<InvoiceSendStatus[]>([]);
    const [sendingInProgress, setSendingInProgress] = useState(false);
    const [isSendingCancelled, setIsSendingCancelled] = useState(false);
    const [shouldClearSelection, setShouldClearSelection] = useState(false);
    const [isLoadingExclude, setIsLoadingExclude] = useState(false);
    const [invoiceInfoList, setInvoiceInfoList] = useState<InvoiceCompactDTO[]>([]);
    const [needsConfirmation, setNeedsConfirmation] = useState(false);
    const [confirmed, setConfirmed] = useState(false);

    const scrollRefs = useRef<Record<number, HTMLDivElement | null>>({});
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    // Используем ref для отслеживания отмены во время выполнения
    const isCancelledRef = useRef<boolean>(false);
    const currentOrderIndexRef = useRef<number>(0);
    const allowedOrdersRef = useRef<InvoiceCompactDTO[]>([]);
    // Сохраняем ID уже отправленных инвойсов, чтобы не отправлять их повторно при следующем открытии
    const sentInvoicesIdsRef = useRef<Set<number>>(new Set());

    // Загружаем список exclude при открытии модального окна
    useEffect(() => {
        if (isOpen) {
            // При открытии модального окна очищаем все ref-ы, чтобы начать с чистого листа
            sentInvoicesIdsRef.current.clear();
            allowedOrdersRef.current = [];
            currentOrderIndexRef.current = 0;
            isCancelledRef.current = false;
            loadExcludeList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, orders]);

    // Функция для загрузки информации об инвойсах с бэкенда
    const loadExcludeList = async () => {
        setIsLoadingExclude(true);
        try {
            const singleReqConfig = buildFilters();
            singleReqConfig.data["reminder"] = !!flagReminder;
            const result = await orderController.getInvoiceInfo(singleReqConfig);
            console.log(result);
            // result может быть Option<InvoiceCompactDTO[]>
            if (result && !result.None && Array.isArray(result.Some)) {
                setInvoiceInfoList(result.Some);
                const count = result.Some.filter(order => invoiceHasLocalPrintProof(order)).length;
                console.log("count", count);
                if (count > 30) {
                    setNeedsConfirmation(true);
                    setConfirmed(false);
                    console.log("2 count", isLoadingExclude, needsConfirmation, confirmed);
                } else {
                    setNeedsConfirmation(false);
                    setConfirmed(false);
                }
            } else if (Array.isArray(result)) {
                setInvoiceInfoList(result);
                const count = result.filter(order => invoiceHasLocalPrintProof(order)).length;
                if (count > 30) {
                    setNeedsConfirmation(true);
                    setConfirmed(false);
                } else {
                    setNeedsConfirmation(false);
                    setConfirmed(false);
                }
            }
        } catch (error) {
            console.error('Failed to load invoice info:', error);
        } finally {
            setIsLoadingExclude(false);
            console.log("3 count", isLoadingExclude, needsConfirmation, confirmed);
        }
    };
    // Функция для отправки инвойсов
    const handleSendInvoices = async () => {
        console.log("invoiceInfoList", invoiceInfoList);
        // Проверяем, что инвойсы были напечатаны
        const allowedOrders: InvoiceCompactDTO[] = [];

        for (const order of invoiceInfoList) {
            // Не напечатанные инвойсы будут добавлены в список с предупреждением
            // if (order.printPressed === 0) {
            //     // Не добавляем в allowedOrders, но добавим в статусы с предупреждением
            //     continue;
            // }
            allowedOrders.push(order);
        }

        console.log("allowedOrders", allowedOrders);
        // Если нет разрешенных инвойсов для отправки, закрываем модальное окно
        if (allowedOrders.length === 0) {
            onClose();
            return;
        }

        // Инициализируем статусы для разрешенных инвойсов
        setInvoiceSendStatuses(
            allowedOrders.map(order => {
                return {
                    id: Number(order.id),
                    dealershipName: order?.dealershipName || 'Unknown',
                    status: invoiceHasLocalPrintProof(order) ? 'pending' : 'warning' as const,
                    error: invoiceHasLocalPrintProof(order) ? '' : 'Print invoice or Stripe payment details summary at least once'
                };
            })
        );

        // Если требуется подтверждение, не начинаем отправку
        if (needsConfirmation && !confirmed) {
            return;
        }

        // Если подтверждение не требуется, начинаем отправку
        startSending(allowedOrders);
    };

    // Функция для начала отправки (после подтверждения)
    const startSending = async (allowedOrders: any[]) => {
        console.log("startSending called with", allowedOrders.length, "orders");
        console.log("allowedOrders:", allowedOrders);

        setIsSendingCancelled(false);
        setShouldClearSelection(false);
        setSendingInProgress(true);
        setNeedsConfirmation(false);
        setConfirmed(true);

        // Сохраняем ссылки для использования в цикле
        isCancelledRef.current = false;
        allowedOrdersRef.current = allowedOrders;
        currentOrderIndexRef.current = 0;

        // Отправляем только разрешенные инвойсы (не включая уже отправленные)
        for (let i = currentOrderIndexRef.current; i < allowedOrdersRef.current.length; i++) {
            const order = allowedOrdersRef.current[i];
            currentOrderIndexRef.current = i;

            // Проверяем, не была ли отправка отменена
            if (isCancelledRef.current) {
                // Помечаем только те инвойсы, которые еще не были обработаны (status: 'pending')
                const remainingOrders = allowedOrders.slice(i);
                setInvoiceSendStatuses(prev =>
                    prev.map(status => {
                        const isRemaining = remainingOrders.some(rem => rem.id === status.id);
                        // Помечаем как ошибку только если статус еще 'pending'
                        if (isRemaining && status.status === 'pending') {
                            return {...status, status: 'error', error: 'Stopped by user'};
                        }
                        return status;
                    })
                );
                break;
            }

            // Проверяем отмену еще раз
            if (isCancelledRef.current) {
                // Помечаем только те инвойсы, которые еще не были обработаны (status: 'pending')
                const remainingOrders = allowedOrdersRef.current.slice(i);
                setInvoiceSendStatuses(prev =>
                    prev.map(status => {
                        const isRemaining = remainingOrders.some(rem => rem.id === status.id);
                        // Помечаем как ошибку только если статус еще 'pending'
                        if (isRemaining && status.status === 'pending') {
                            return {...status, status: 'error', error: 'Stopped by user'};
                        }
                        return status;
                    })
                );
                break;
            }

            try {
                // Обновляем статус на "отправляется"
                const oid = Number(order.id);
                setInvoiceSendStatuses(prev =>
                    prev.map(status =>
                        status.id === oid
                            ? {...status, status: 'sending'}
                            : status
                    )
                );

                // Скроллим к текущему элементу
                setTimeout(() => {
                    const element = scrollRefs.current[oid];
                    if (element) {
                        element.scrollIntoView({behavior: 'smooth', block: 'center'});
                    }
                }, 100);

                // Создаем конфиг для конкретного инвойса
                const singleReqConfig = buildFilters(order);
                singleReqConfig.data["reminder"] = !!flagReminder;

                // НЕ проверяем отмену перед отправкой - текущее письмо должно отправиться до конца
                const result = await orderController.sendInvoice(singleReqConfig);

                // Проверяем отмену после отправки - только после завершения текущего письма
                if (isCancelledRef.current) {
                    // Помечаем текущее письмо как успешно отправленное (если успешно)
                    if (!result.None) {
                        setInvoiceSendStatuses(prev =>
                            prev.map(status =>
                                status.id === oid
                                    ? {...status, status: 'success'}
                                    : status
                            )
                        );
                    } else {
                        setInvoiceSendStatuses(prev =>
                            prev.map(status =>
                                status.id === oid
                                    ? {...status, status: 'error', error: 'Failed to send'}
                                    : status
                            )
                        );
                    }
                    // Останавливаем отправку со следующего письма
                    break;
                }

                if (result.None) {
                    // Ошибка отправки
                    setInvoiceSendStatuses(prev =>
                        prev.map(status =>
                            status.id === oid
                                ? {...status, status: 'error', error: 'Failed to send'}
                                : status
                        )
                    );
                } else {
                    // Успешная отправка
                    // Сохраняем ID отправленного инвойса в ref
                    sentInvoicesIdsRef.current.add(oid);
                    setInvoiceSendStatuses(prev =>
                        prev.map(status =>
                            status.id === oid
                                ? {...status, status: 'success'}
                                : status
                        )
                    );
                }
            } catch (error: unknown) {
                // Проверяем отмену при ошибке
                if (isCancelledRef.current) {
                    break;
                }

                let errorMessage = 'Unknown error';
                if (error instanceof Error) {
                    errorMessage = error.message;
                } else if (typeof error === 'string') {
                    errorMessage = error;
                }

                const errOid = Number(order.id);
                setInvoiceSendStatuses(prev =>
                    prev.map(status =>
                        status.id === errOid
                            ? {...status, status: 'error', error: errorMessage}
                            : status
                    )
                );
            }
        }

        setSendingInProgress(false);

        // Проверяем, есть ли успешно отправленные инвойсы
        const successCount = invoiceSendStatuses.filter(s => s.status === 'success').length;
        setShouldClearSelection(successCount > 0);

        // Показываем итоговый тост
        // const failedCount = invoiceSendStatuses.filter(s => s.status === 'error').length;
        // toast({
        //     title: successCount > 0 ? `Sent ${successCount} invoice(s)` : 'Sending completed',
        //     description: failedCount > 0 ? `${failedCount} invoice(s) failed to send` : undefined,
        //     status: failedCount === 0 && successCount > 0 ? 'success' : successCount === 0 ? 'error' : 'warning',
        //     duration: 5000,
        //     isClosable: true,
        // });

        // Вызываем callback при успехе
        if (onSuccess && successCount > 0) {
            onSuccess();
        }
    };

    // Сбрасываем состояния при закрытии модального окна
    useEffect(() => {
        if (!isOpen) {
            // Если модальное окно закрыто, полностью сбрасываем все состояния
            // При следующем открытии модальное окно будет работать как в первый раз
            resetAllStates();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Автоматически запускаем отправку при открытии модального окна и загрузке exclude списка
    useEffect(() => {
        if (isOpen && invoiceInfoList.length > 0 && !sendingInProgress && invoiceSendStatuses.length === 0 && !isLoadingExclude) {
            // handleSendInvoices инициализирует статусы и проверит needsConfirmation внутри
            handleSendInvoices();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, isLoadingExclude, invoiceInfoList]);

    // Функция для закрытия модального окна
    const handleCloseModal = () => {
        if (sendingInProgress) {
            // Если отправка в процессе, останавливаем отправку и закрываем
            handleStopSending();
            // Даем время для завершения текущего письма, затем закрываем
            setTimeout(() => {
                resetAllStates();
                onClose();
                if (shouldClearSelection && onSuccess) {
                    onSuccess();
                }
            }, 500);
        } else {
            // Если отправка завершена, закрываем и сбрасываем состояние
            resetAllStates();
            onClose();
            if (shouldClearSelection && onSuccess) {
                onSuccess();
            }
        }
    };

    // Функция для сброса всех состояний
    const resetAllStates = () => {
        // Полностью сбрасываем все состояния, включая ref с отправленными ID
        // При следующем открытии модальное окно будет работать как в первый раз
        sentInvoicesIdsRef.current.clear();
        setInvoiceSendStatuses([]);
        setInvoiceInfoList([]);
        setIsSendingCancelled(false);
        setShouldClearSelection(false);
        setSendingInProgress(false);
        setNeedsConfirmation(false);
        setConfirmed(false);
        isCancelledRef.current = false;
        currentOrderIndexRef.current = 0;
        allowedOrdersRef.current = [];
    };

    // Функция для подтверждения отправки
    const handleConfirmSending = () => {
        console.log("handleConfirmSending called");
        // Получаем список разрешенных инвойсов из invoiceInfoList (только напечатанные)
        const allowedOrders = invoiceInfoList.filter(order => invoiceHasLocalPrintProof(order));
        console.log("allowedOrders for confirmation:", allowedOrders);

        if (allowedOrders.length === 0) {
            console.log("No allowed orders, closing modal");
            onClose();
            return;
        }

        // Убеждаемся, что статусы инициализированы
        if (invoiceSendStatuses.length === 0) {
            // Инициализируем статусы для разрешенных инвойсов
            setInvoiceSendStatuses(
                allowedOrders.map(order => {
                    return {
                        id: Number(order.id),
                        dealershipName: order?.dealershipName || 'Unknown',
                        status: 'pending' as const
                    };
                })
            );

            // Добавляем не напечатанные инвойсы как предупреждения
            const unprintedOrders = invoiceInfoList.filter(order => !invoiceHasLocalPrintProof(order));
            if (unprintedOrders.length > 0) {
                setInvoiceSendStatuses(prev => [
                    ...prev,
                    ...unprintedOrders.map(order => {
                        return {
                            id: Number(order.id),
                            dealershipName: order?.dealershipName || 'Unknown',
                            status: 'warning' as const,
                            error: 'Print invoice or Stripe payment details summary at least once'
                        };
                    })
                ]);
            }
        }

        console.log("Starting send with", allowedOrders.length, "orders");
        startSending(allowedOrders);
    };

    // Функция для остановки отправки
    const handleStopSending = () => {
        isCancelledRef.current = true;
        setIsSendingCancelled(true);
        setSendingInProgress(false);
        toast({
            title: 'Sending stopped',
            status: 'info',
            duration: 3000,
            isClosable: true,
        });
    };

    // Функция для скролла к текущему отправляемому элементу
    const scrollToCurrent = () => {
        const currentSending = invoiceSendStatuses.find(s => s.status === 'sending');
        if (currentSending && scrollRefs.current[currentSending.id]) {
            scrollRefs.current[currentSending.id]?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }
    };

    // Автоматический скролл при изменении статуса
    useEffect(() => {
        if (sendingInProgress) {
            scrollToCurrent();
        }
    }, [invoiceSendStatuses, sendingInProgress]);

    // Вычисляем прогресс на основе статусов
    const totalCount = invoiceSendStatuses.length;
    const completedCount = invoiceSendStatuses.filter(s => s.status === 'success' || s.status === 'error' || s.status === 'warning').length;
    const progress = totalCount > 0
        ? Math.min(100, Math.round((completedCount / totalCount) * 100))
        : 0;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleCloseModal}
            size="lg"
            closeOnOverlayClick={true}
            scrollBehavior="inside"
        >
            <ModalOverlay/>
            {/* @ts-ignore - сложный тип ModalContent */}
            <ModalContent maxH="80vh">
                <Box display="flex" flexDirection="column" height="100%">
                    <ModalHeader pb={2}>
                        <Flex alignItems="center" justifyContent="space-between">
                            <Text>Sending Invoices ({invoiceSendStatuses.length})</Text>
                            <HStack spacing={2}>
                                {sendingInProgress && (
                                    <IconWithTooltip
                                        icon={MdStop}
                                        tooltipMessage="Stop"
                                        onClick={handleStopSending}
                                        fontSize="24px"
                                        color="red.500"
                                    />
                                )}
                                <CloseButton
                                    onClick={handleCloseModal}
                                />
                            </HStack>
                        </Flex>
                    </ModalHeader>

                    <ModalBody flex={1} overflow="auto" p={0}>
                        {isLoadingExclude && (
                            <Box p={4} textAlign="center">
                                <Spinner size="md"/>
                                <Text mt={2} fontSize="sm" color="gray.600">
                                    Loading exclude list...
                                </Text>
                            </Box>
                        )}

                        {!isLoadingExclude && needsConfirmation && !confirmed && (
                            <Box pt={4} px={4} pb={2} textAlign="center">
                                <VStack spacing={3}>
                                    <Box>
                                        <Text fontSize="md" fontWeight="bold" color="orange.600" mb={1}>
                                            Warning
                                        </Text>
                                        <Text fontSize="sm" color="gray.700">
                                            You are about to send {invoiceSendStatuses.filter(s => s.status === 'pending').length} invoices.
                                        </Text>
                                        <Text fontSize="xs" color="gray.600" mt={1}>
                                            Are you sure you want to proceed?
                                        </Text>
                                    </Box>
                                    <Button
                                        colorScheme="green"
                                        size="xs"
                                        onClick={handleConfirmSending}
                                    >
                                        Confirm and Send
                                    </Button>
                                </VStack>
                            </Box>
                        )}

                        {!isLoadingExclude && (
                            <>
                                <Box p={4} pb={0}>
                                    <VStack spacing={4} align="stretch">
                                        {/* Прогресс бар */}
                                        <Box>
                                            <Flex justifyContent="space-between" mb={2}>
                                                <Text fontSize="sm">
                                                    Progress: {completedCount} of {totalCount}
                                                </Text>
                                                <Text fontSize="sm" fontWeight="bold">
                                                    {progress}%
                                                </Text>
                                            </Flex>
                                            <Progress
                                                value={progress}
                                                size="sm"
                                                colorScheme={
                                                    isSendingCancelled ? 'gray' :
                                                        sendingInProgress ? "blue" : "green"
                                                }
                                                hasStripe
                                                isAnimated={sendingInProgress && !isSendingCancelled}
                                            />
                                        </Box>

                                        <Divider/>

                                        {/* Кнопка скролла к текущему */}
                                        {sendingInProgress && (
                                            <Button
                                                size="xs"
                                                variant="ghost"
                                                onClick={scrollToCurrent}
                                                leftIcon={<FiArrowDown/>}
                                                alignSelf="flex-start"
                                            >
                                                Scroll to current
                                            </Button>
                                        )}
                                    </VStack>
                                </Box>

                                {/* Список инвойсов */}
                                <Box
                                    ref={scrollContainerRef}
                                    maxH="400px"
                                    overflowY="auto"
                                    px={4}
                                    pb={4}
                                    mb={4}
                                >
                                    <VStack spacing={2} align="stretch">
                                        {invoiceSendStatuses.map((invoiceStatus) => (
                                            <Box
                                                key={invoiceStatus.id}
                                                ref={(el: HTMLDivElement | null) => scrollRefs.current[invoiceStatus.id] = el}
                                            >
                                                <Flex
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    p={3}
                                                    borderRadius="md"
                                                    bg={
                                                        invoiceStatus.status === 'error' ? 'red.50' :
                                                            invoiceStatus.status === 'success' ? 'green.50' :
                                                                invoiceStatus.status === 'sending' ? 'blue.50' :
                                                                    invoiceStatus.status === 'warning' ? 'orange.50' : 'gray.50'
                                                    }
                                                    borderWidth={1}
                                                    borderColor={
                                                        invoiceStatus.status === 'error' ? 'red.200' :
                                                            invoiceStatus.status === 'success' ? 'green.200' :
                                                                invoiceStatus.status === 'sending' ? 'blue.200' :
                                                                    invoiceStatus.status === 'warning' ? 'orange.200' : 'gray.200'
                                                    }
                                                    position="relative"
                                                >
                                                    {/* Индикатор текущего элемента */}
                                                    {invoiceStatus.status === 'sending' && (
                                                        <Box
                                                            position="absolute"
                                                            left="-8px"
                                                            top="50%"
                                                            transform="translateY(-50%)"
                                                            width="4px"
                                                            height="70%"
                                                            bg="blue.500"
                                                            borderRadius="full"
                                                        />
                                                    )}

                                                    <Flex flexDirection="column" alignItems="flex-start" flex={1}>
                                                        <Flex alignItems="center">
                                                            {invoiceStatus.status === 'success' && (
                                                                <Box mr={3}>
                                                                    <FiCheck color="green" size={18}/>
                                                                </Box>
                                                            )}
                                                            {invoiceStatus.status === 'error' && (
                                                                <Box mr={3}>
                                                                    <FiX color="red" size={18}/>
                                                                </Box>
                                                            )}
                                                            {invoiceStatus.status === 'warning' && (
                                                                <Box mr={3}>
                                                                    <FiAlertTriangle color="orange" size={18}/>
                                                                </Box>
                                                            )}
                                                            {invoiceStatus.status === 'sending' && (
                                                                <Box mr={3}>
                                                                    <Spinner size="sm"/>
                                                                </Box>
                                                            )}
                                                            {invoiceStatus.status === 'pending' && (
                                                                <Box mr={3} width="18px"/>
                                                            )}
                                                            <Text fontWeight="medium">
                                                                #{invoiceStatus.id}
                                                            </Text>
                                                            {invoiceStatus.dealershipName && (
                                                                <Text fontSize="xs" color="gray.600" ml={2}>
                                                                    ({invoiceStatus.dealershipName})
                                                                </Text>
                                                            )}
                                                        </Flex>
                                                        {(invoiceStatus.status === 'error' || invoiceStatus.status === 'warning') && invoiceStatus.error && (
                                                            <Text fontSize="xs"
                                                                  color={invoiceStatus.status === 'warning' ? 'orange.600' : 'red.600'}
                                                                  mt={1} ml={9}>
                                                                {invoiceStatus.error}
                                                            </Text>
                                                        )}
                                                    </Flex>

                                                    <Badge
                                                        colorScheme={
                                                            invoiceStatus.status === 'success' ? 'green' :
                                                                invoiceStatus.status === 'error' ? 'red' :
                                                                    invoiceStatus.status === 'warning' ? 'orange' :
                                                                        invoiceStatus.status === 'sending' ? 'blue' : 'gray'
                                                        }
                                                        variant="subtle"
                                                        ml={2}
                                                        minWidth="80px"
                                                        textAlign="center"
                                                    >
                                                        {invoiceStatus.status === 'success' && 'Sent'}
                                                        {invoiceStatus.status === 'error' && 'Failed'}
                                                        {invoiceStatus.status === 'warning' && 'Not Printed'}
                                                        {invoiceStatus.status === 'sending' && 'Sending...'}
                                                        {invoiceStatus.status === 'pending' && 'Pending'}
                                                    </Badge>
                                                </Flex>
                                            </Box>
                                        ))}
                                    </VStack>
                                </Box>

                                {/* Сообщения об ошибках */}
                                {invoiceSendStatuses.some(s => s.status === 'error') && (
                                    <Box p={3} mb={4} bg="red.50" borderTopWidth={1} borderColor="red.200">
                                        <Text fontSize="sm" color="red.600">
                                            Some invoices failed to send. Please try again or contact support.
                                        </Text>
                                    </Box>
                                )}
                            </>
                        )}
                    </ModalBody>
                </Box>
            </ModalContent>
        </Modal>
    );
};

